<script setup>
import { computed, ref, inject } from 'vue'

import { useStore } from 'vuex'
import _ from 'lodash'
import FormButton from '../Atoms/FormButton.vue'
import IconBase from '../icon/IconBase.vue'
import IconSearch from '../icon/IconSearch.vue'
import IconCheckmark from '../icon/IconCheckmark.vue'
import IconCross from '../icon/IconCross.vue'
import IconCameraFilled from '../icon/IconCameraFilled.vue'
import IconPlus from '../icon/IconPlus.vue'
import FormInput from '../Atoms/FormInput.vue'
import Tooltip from '../Atoms/Tooltip.vue'
import FormCheckbox from '../Atoms/FormCheckbox.vue'
import FormRadio from '../Atoms/FormRadio.vue'
import PaginatedList from './PaginatedList.vue'
import IconSearchEmpty from '../icon/IconSearchEmpty.vue'

const store = useStore()
const playlistContentSelection = inject('playlistContentSelection', null)

// Props
const props = defineProps({
	multiview: {
		type: Boolean,
		default: false
	},
	source: {
		type: String,
	},
	disabledStreamIds: {
		type: Array,
		default: () => []
	},
	disabledTooltip: {
		type: String,
		default: ''
	},
	variant: {
		type: String,
		default: 'default'
	},
	itemsPerPage: {
		type: Number,
		default: 5,
	},
	itemsPerPageOptions: {
		type: Array,
		default: () => [5, 10, 20],
	},
	showItemsPerPageSelector: {
		type: Boolean,
		default: false,
	},
	// _id of the currently selected/linked stream (shows a check instead of "+")
	selectedId: {
		type: String,
		default: null,
	},
})

// Emits
const emit = defineEmits({
	'add-livestream': () => true
})

//Refs 
const search = ref('')
const selectedStream = ref(null)

// Computed
const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])
const playoutConfig = computed(() => store.getters['Playout/playoutConfig'])
const shouldShowAddButton = computed(() => props.source !== 'showcase' && props.variant !== 'modern')
const shouldShowCheckbox = computed(() => props.source === 'playlist')
const shouldShowRadio = computed(() => props.source === 'dynamic')

const liveStreamsFiltered = computed(() => {
	const liveStreams = _.cloneDeep(allInOneStreams.value.filter(stream => stream.name.toLowerCase().includes(search.value.toLowerCase())))
	return liveStreams.sort(function (a, b) {
		return new Date(b.creationTime) - new Date(a.creationTime)
	})
})

const hasSearchQuery = computed(() => search.value.trim().length > 0)

const showSearchEmptyState = computed(
	() => hasSearchQuery.value && liveStreamsFiltered.value.length === 0,
)

// The simple "add" sources (and dynamic) get a fully clickable row; playlist
// uses its checkbox, showcase/modern have no add affordance.
const isRowClickable = computed(() => props.source !== 'playlist' && (props.source === 'dynamic' || shouldShowAddButton.value))

const listClasses = computed(() => {
	// Modern (modal) lets the modal own the scroll with a stable gutter, so no
	// inner max-height; other placements keep a bounded, internally-scrolling list.
	const height = props.variant === 'modern' ? '' : (props.multiview ? 'max-h-[400px]' : 'max-h-[300px]')
	const variantClass = props.variant === 'modern' ? 'grid grid-cols-1 gap-2' : 'flex flex-col gap-0.5'
	const basicClass = 'overflow-y-auto overflow-x-hidden'
	return `${basicClass} ${height} ${variantClass}`
})

const listItemClasses = computed(() => {
	const variantClass = props.variant === 'modern'
		? 'h-12 bg-surface-3 gap-2 !p-2 !rounded-md !border !border-transparent text-surface-text-2'
		: `hover:bg-surface-3 rounded-md py-1.5 gap-x-2 px-2 min-h-9 text-surface-8 transition-colors ${isRowClickable.value ? 'cursor-pointer' : ''}`
	const basicClass = ' flex items-center'
	const activeStateClass = props.variant === 'modern' ? 'has-[input:checked]:!border-main has-[input:checked]:bg-main-transparent' : ''
	return `${basicClass} ${variantClass} ${activeStateClass}`
})


// Methods
const clearSearch = () => search.value = ''

const addFile = (file) => {
	emit('add-livestream', file)
}

const isDestination = (streamId) => streamId === playoutConfig.value?.destination

const isChecked = (stream) => {
	if (props.source === 'playlist' && playlistContentSelection) {
		return playlistContentSelection.isSelected(stream, 'livestream')
	}
	if (props.source === 'dynamic') {
		return selectedStream.value === stream._id
	}
	return false
}

const isDisabledByProp = (streamId) => props.disabledStreamIds.includes(streamId)
const getDisabledTooltip = (stream) => {
	if (isDestination(stream?._id)) {
		return 'This stream is set as destination for this TV Playout and cannot be selected.'
	}
	if (isDisabledByProp(stream?._id)) {
		return props.disabledTooltip || 'This stream is already linked to this media flow and cannot be selected.'
	}
	return ''
}
const isStreamDisabled = (stream) => isDestination(stream?._id) || isDisabledByProp(stream?._id)
const toggleStream = (stream) => {
	if (props.source === 'playlist' && playlistContentSelection) {
		playlistContentSelection.toggle(stream, 'livestream')
		return
	}
	if (props.source === 'playlist') {
		emit('add-livestream', stream)
		return
	}
	if (props.source === 'dynamic') {
		selectedStream.value = stream._id
		emit('add-livestream', stream)
	}
}

// Whole-row click for clickable sources (dynamic toggles its radio; the simple
// "add" sources add the stream). Playlist/showcase keep their own controls.
const onRowClick = (stream) => {
	if (!isRowClickable.value || isStreamDisabled(stream)) return
	if (props.source === 'dynamic') {
		toggleStream(stream)
		return
	}
	addFile(stream)
}
</script>

<template>
	<section v-if="allInOneStreams.length === 0" class="min-h-28">
		<h3 class="text-sm text-surface-8 font-medium text-center mt-5">You have no livestreams yet</h3>
		<p class="text-center my-3">
			<FormButton to="/">
				Open Livestreams
			</FormButton>
		</p>
	</section>
	<section v-else>
		<div class="flex items-center mb-3 relative" :class="{ 'mt-3': multiview }">
			<icon-base class="absolute z-10 left-2 text-surface-7">
				<icon-search />
			</icon-base>
			<FormInput placeholder="Search" customClasses="!px-8" v-model="search" />
			<FormButton class="absolute z-10 right-0" isIcon type="link" v-if="search" @click="clearSearch">
				<icon-base>
					<icon-cross />
				</icon-base>
			</FormButton>
		</div>
		<slot />
		<section v-if="showSearchEmptyState" class="min-h-48 text-center flex flex-col items-center justify-center gap-y-2">
			<icon-base width="64" height="64" widthViewBox="64" heightViewBox="64">
				<icon-search-empty />
			</icon-base>
			<p class="text-sm text-surface-8 font-medium">No results found</p>
			<p class="text-xs text-surface-6">Try adjusting your search to find what you’re looking for</p>
		</section>
		<PaginatedList v-else-if="liveStreamsFiltered.length > 0" :items="liveStreamsFiltered" :page-size="itemsPerPage"
			:source="source" :page-size-options="itemsPerPageOptions" :show-page-size-selector="showItemsPerPageSelector"
			:list-class="listClasses" item-key="_id">
			<template #default="{ item: stream }">
				<!-- Modern variant: card row with icon tile -->
				<li v-if="props.variant === 'modern'" :title="stream.name"
					class="group relative flex items-center gap-3 p-2.5 rounded-lg bg-surface-3 hover:bg-surface-4 ring-1 ring-inset ring-transparent cursor-pointer transition-colors has-[input:checked]:!ring-main has-[input:checked]:!bg-main-transparent"
					:class="isStreamDisabled(stream) ? '!opacity-50 !cursor-not-allowed hover:!bg-surface-3' : ''"
					@click="onRowClick(stream)">
					<FormRadio v-if="shouldShowRadio" class="sr-only absolute opacity-0 pointer-events-none w-0 h-0"
						name="dynamic-stream-source" :modelValue="selectedStream" :inputValue="stream._id"
						@update:modelValue="toggleStream(stream)" />
					<FormCheckbox v-if="shouldShowCheckbox" class="shrink-0" :modelValue="isChecked(stream)"
						@change="toggleStream(stream)" />
					<span class="shrink-0 grid place-items-center size-9 rounded-md bg-surface-4 text-surface-7 group-hover:text-white transition-colors">
						<icon-base fill="none">
							<icon-camera-filled />
						</icon-base>
					</span>
					<Tooltip :active="!!getDisabledTooltip(stream)" wrapperClass="min-w-0 flex-1"
						:content="getDisabledTooltip(stream)">
						<span class="truncate text-sm font-medium text-surface-text-1">{{ stream.name }}</span>
					</Tooltip>
					<icon-base v-if="selectedId && stream._id === selectedId" class="shrink-0 text-main-light">
						<icon-checkmark />
					</icon-base>
				</li>

				<!-- Default variant -->
				<li v-else :title="stream.name" :class="listItemClasses" @click="onRowClick(stream)">
					<FormCheckbox v-if="shouldShowCheckbox" :modelValue="isChecked(stream)" @change="toggleStream(stream)" />
					<FormRadio v-if="shouldShowRadio" class="sr-only absolute opacity-0 pointer-events-none w-0 h-0"
						name="dynamic-stream-source"
						:modelValue="selectedStream"
						:inputValue="stream._id"
						@update:modelValue="toggleStream(stream)" />
					<Tooltip :active="!!getDisabledTooltip(stream)" wrapperClass="w-full min-w-0"
						:content="getDisabledTooltip(stream)">
						<div class="flex gap-x-2 items-center w-full">
							<icon-base class="shrink-0 text-surface-7">
								<icon-camera-filled />
							</icon-base>
							<span class="text-sm grow-0 truncate">{{ stream.name }}</span>
							<!-- Selected -->
							<icon-base v-if="selectedId && stream._id === selectedId" class="shrink-0 ml-auto text-main-light">
								<icon-checkmark />
							</icon-base>
							<!-- Add -->
							<FormButton v-else-if="shouldShowAddButton" :disabled="isStreamDisabled(stream)" size="sm" isIcon
								:type="multiview ? 'main' : 'link'"
								:class="multiview ? 'shrink-0 ml-auto' : 'shrink-0 ml-auto !text-surface-6 hover:!text-main-light'"
								@click.stop="addFile(stream)">
								<icon-base>
									<icon-plus />
								</icon-base>
							</FormButton>
						</div>
					</Tooltip>
				</li>
			</template>
		</PaginatedList>
	</section>
</template>

<style scoped></style>